import type { NotificationEvent } from '../types/notification';

/**
 * 알림 source 판별 — 2026-08-12 사용자 요구.
 *
 *   "채팅으로 들어온 내역은 알림에 넣지 말고 채팅 상태바에만 넣어달라."
 *
 * 채팅 미읽음은 이미 채팅 도메인이 자체 배지로 알린다(상태바 채팅 항목 + 드롭다운 방별 배지).
 * 같은 사건을 알림 벨이 또 세면 사용자가 같은 것을 두 번 지워야 한다.
 *
 * 두 층에서 함께 막는다 — 어느 한쪽만으로는 새는 구멍이 남는다:
 *   1. 서버 — 알림함 목록·미읽음 카운트 조회에서 제외
 *      (`notifications-helpers.buildHiddenSourceWhere`). 새로고침 후의 진실.
 *   2. 여기(클라) — 실시간 `sse.notification` 이 도착했을 때 벨 카운터를 올리지 않는다.
 *      서버만 막으면 세션 도중 도착한 채팅 알림이 배지를 올려, 벨을 눌러도 목록은 빈 채로
 *      숫자만 남는다.
 *
 * ⚠ notification row 자체는 계속 만들어진다 — 그게 오프라인 기기 푸시의 근거다.
 *   여기서 거르는 건 "인앱 알림함 표시" 뿐이다.
 */

/** 채팅 발화 알림의 source 값 — 서버 `chat-bg-push.ts` 의 enqueue 와 같은 문자열. */
export const CHAT_NOTIFICATION_SOURCE = 'chat';

/*
 * ★★★ 회의록 알림 — ⭐ 서버가 다는 ★ «그 글자»다 (2026-10-09 · A-1b).
 *
 * `apps/chat-server/src/modules/voice/voice-finalize.ts` 가 전사 완료·요약 완료에
 * `enqueueNotification` 을 ★ 한 번 부르고 ⇒ 그 세 글자를 ⭐ 그대로 쓴다.
 * ⇒ ⚠ 두 벌로 적은 것이므로 ★ `minutes-notify-wire.test.tsx` 가 ⭐ **그 서버 파일을 읽어**
 *   글자를 맞댄다 — ⇒ ⛔ 서버가 이름을 바꾸면 ★ 조용히 «안 듣게» 되기 때문이다.
 *   (앱도 같은 것을 한다 — `aos/.../MinutesResync.kt` 의 `MinutesNotifyTest`)
 *
 * ## ⛔ `source` 와 `type` 을 ★ «둘 다» 본다
 *
 * ⇒ ★ `type` 만 보면 ⇒ ⚠ 뒷날 **다른 영역이 같은 동사**를 쓸 때 ⭐ 헛돈다.
 *   이 레포의 알림 `type` 은 `<영역>.<대상>.<동사>` 본이고 ★ `approval.doc.*` 처럼
 *   ⭐ 같은 «대상·동사»를 쓰는 영역이 ★ 이미 있다.
 * ⇒ ✅ 앱이 그 꼴을 먼저 골랐고 주석이 까닭을 적었다 — 「★ 다른 기능의 알림에 ⭐ 안 돈다」.
 */
export const MINUTES_NOTIFY_SOURCE = 'minutes_document';
export const MINUTES_NOTIFY_CREATED = 'minutes.doc.created';
export const MINUTES_NOTIFY_SUMMARIZED = 'minutes.doc.summarized';

/**
 * 회의록 목록·상세를 ★ 다시 읽어야 하는 알림인가.
 *
 * ⚠ 전사 완료(`created`)와 요약 완료(`summarized`)를 ⛔ **가리지 않는다** — 둘 다 목록을 바꾸고,
 *   ★ 열어 둔 상세의 요약이 바뀌는 것은 `summarized` 뿐이지만 ⇒ ⭐ 화면이 «다시 읽어 보고»
 *   판단하므로 ★ 같게 다뤄도 결과가 같다.
 * ⇒ ⚠ 알고 둔 대가: 상세를 열어 둔 때 ★ `created` 알림에도 ⭐ 한 번 더 읽는다(헛조회 한 번).
 *   ⇒ 앱이 같은 대가를 적어 뒀다(A-1a).
 */
export function isMinutesNotification(n: Pick<NotificationEvent, 'source' | 'type'> | null | undefined): boolean {
  return n?.source === MINUTES_NOTIFY_SOURCE
    && (n.type === MINUTES_NOTIFY_CREATED || n.type === MINUTES_NOTIFY_SUMMARIZED);
}

/** 알림함에서 감출 알림인가(= 채팅 발화인가). */
export function isHiddenNotification(n: Pick<NotificationEvent, 'source'> | null | undefined): boolean {
  return n?.source === CHAT_NOTIFICATION_SOURCE;
}
